<template>
  <v-sheet
    class="ma-1 pa-0"
    color="white"
    height="100%"
    max-width="500px"
    min-width="420px"
  >
    <v-container fluid fill-width class="pa-0 ma-0">
      <!-- Variable icon/title/reset button -->
      <v-row
        class="subheading primary--text text--darken-4 pa-0 ma-0 text-truncate not-hightlight-var pt-1"
      >
        <v-col cols="12" class="ma-0 pa-0">
          <v-card flat>
            <v-card-title class="primary--text pl-3 py-2">
              <img
                :src="'/images/' + variable.category + '-icon-128.png'"
                :title="variable.category"
                style="height: 3em;"
                @error="missingDomainFallback"
              />

              <div
                class="text-h6 ml-2 text-wrap pl-1"
                style="max-width: 65%; word-break: keep-all;"
              >
                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      {{ getChartTitle(variable, 'abbreviation') }}
                    </span>
                  </template>
                  <span>{{ getChartTitle(variable, 'label') }}</span>
                </v-tooltip>
              </div>

              <v-spacer />

              <v-btn
                v-if="interactive"
                class="primary text--white ma-0 px-2 py-0"
                @click="resetClicked"
                >Reset</v-btn
              >
              <span
                v-if="isBelowPValThreshold(variable)"
                style="color: red; font-size: 3rem;"
                >*</span
              >
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
      <!-- End of Variable icon/title/reset button -->
    </v-container>

    <v-container fluid fill-width class="pa-0 ma-0">
      <v-row class="pa-0 ma-0">
        <v-col cols="12" class="pa-0 ma-0">
          <div v-if="variable.value_type === 'date'" class="pl-3">
            Date types not supported.
          </div>
          <MultiVarBarChart
            v-else-if="
              !variable.is_longitudinal &&
                variable.data_category === 'Categorical' &&
                chartVariables.length > 1
            "
            :id="variable.id"
            :dimension-name="variable.label"
            :variables="chartVariables"
            show-selection
            :interactive="interactive"
            @userChangedVariable="userChangedOutputVariable"
          />
          <BarChart
            v-else-if="
              variable.is_longitudinal === false &&
                variable.data_category === 'Categorical'
            "
            :id="variable.id"
            :key="'ovcc-' + variable.id"
            :dimension-name="variable.label"
            :variable="variable"
            :interactive="interactive"
            @userChangedVariable="userChangedOutputVariable"
          />
          <HistogramChart
            v-else-if="variable.is_longitudinal === false"
            :id="variable.id"
            :key="'ovhc-' + variable.id"
            :dimension-name="variable.label"
            :input-variable="false"
            :variable="variable"
            :show-best-and-worst="
              variable.type == 'observation' &&
                (!variable.is_longitudinal ||
                  (variable.label == 'First Visit' ||
                    variable.label == 'Last Visit'))
            "
            :interactive="interactive"
            @userChangedVariable="userChangedOutputVariable"
          />
          <MultiChart
            v-else
            :key="
              'ovmc-' + variable.id + '-' + getSelectedMeasuresKey(variable)
            "
            class="ma-1"
            :variable="variable"
            :dimension-name="dimension"
            :highlight-change="isBelowPValThreshold(variable)"
            :first-visit-label="getFirstVisitLabel()"
            :last-visit-label="getLastVisitLabel()"
            :interactive="interactive"
            width="400px"
            @userChangedVariable="userChangedOutputVariable"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script>
import BarChart from '@/components/CohortManager/BarChart/BarChart.vue';
import MultiVarBarChart from '@/components/CohortManager/BarChart/MultiVarBarChart.vue';
import HistogramChart from '@/components/CohortManager/HistogramChart/HistogramChart.vue';
import MultiChart from '@/components/CohortManager/MultiChart.vue';
import { mapActions, mapState } from 'vuex';
import { state, actions } from '@/store/modules/cohortManager/types';
import {
  getSetValuedVariableGroups,
  missingDomainFallback,
} from '@/utils/helpers';

export default {
  components: {
    BarChart,
    MultiVarBarChart,
    HistogramChart,
    MultiChart,
  },
  props: {
    variable: {
      type: Object,
      required: true,
    },
    interactive: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      dimension: null,
      resetCount: 0,
      subjectVariableGroups: { groups: [], var2group: {}, var2var: {} },
      observationVariableGroups: { groups: [], var2group: {}, var2var: {} },
      chartVariables: [],
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('cohortManager', {
      collection: state.COLLECTION,
      pvals: state.PVALS,
      pval_threshold: state.PVAL_THRESHOLD,
      dimensions: state.DIMENSIONS,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
  },
  created() {
    this.subjectVariableGroups = getSetValuedVariableGroups(
      this.collection.subject_variables_list.map(sv => sv.ontology)
    );
    this.observationVariableGroups = getSetValuedVariableGroups(
      this.collection.observation_variables_list.map(ov => ov.ontology)
    );

    if (this.variable.children) {
      this.variable.children.forEach(childVariable => {
        this.addDimensionHelper(childVariable);
      });
      this.dimension = this.variable.id;
    } else {
      this.addDimensionHelper(this.variable);
    }
  },
  methods: {
    ...mapActions('cohortManager', {
      addDimension: actions.ADD_DIMENSION,
      clearFilter: actions.CLEAR_FILTER,
    }),
    resetClicked() {
      this.clearAllFilters();
      this.$emit('userResetOutputVariable', this.dimension);
    },
    userChangedOutputVariable() {
      this.$emit('userChangedOutputVariable', this.dimension);
    },
    clearAllFilters() {
      this.clearFilter({ dimension: this.variable.label });
      // cover all the bases for the MultiChart/longitudinal case
      this.clearFilter({ dimension: this.variable.label + ' - First Visit' });
      this.clearFilter({ dimension: this.variable.label + ' - Last Visit' });
      this.clearFilter({ dimension: this.variable.label + ' - Change' });
      this.clearFilter({
        dimension: this.variable.label + ' - Rate of Change',
      });
    },
    addDimensionHelper(variable) {
      var measure = null;
      var dimensionName = null;
      var dimensionId = null;

      if (variable.is_longitudinal) {
        measure = variable.id.split('-')[0];
        dimensionName = `${variable.parentLabel} - ${variable.label}`;
        dimensionId =
          'parentID' in variable ? variable.parentID : variable.parent_id;
      } else {
        measure = 'value';
        dimensionName = variable.label;
        dimensionId = variable.id;
      }

      let cvars = this.getChartVariables(variable);
      let ncv = cvars.length;
      this.chartVariables = cvars;
      let payload = null;

      // multiple/grouped variables
      if (ncv > 1) {
        payload = {
          dimensionName,
          accessor: d => {
            return cvars.map(cv =>
              cv.is_longitudinal ? d[cv.parentID][measure] : d[cv.id][measure]
            );
          },
          isArray: true,
        };
      } else {
        payload = {
          dimensionName,
          accessor: d => {
            return d[dimensionId][measure];
          },
          isArray: false,
        };
      }

      this.dimension = String(dimensionId).toString();
      this.addDimension(payload);
    },
    isBelowPValThreshold(v) {
      const pvt = this.pval_threshold;
      let rv = false;
      if (!this.pvals) {
        return false;
      }
      this.pvals.forEach(pv => {
        if (
          pv.label === v.label ||
          (pv.label === v.parentLabel && v.label === 'Change')
        ) {
          if (pv.pval != null && pv.pval < pvt) {
            rv = true;
          }
        }
      });
      return rv;
    },
    getOutcomeClass(v) {
      if (this.isBelowPValThreshold(v)) {
        return 'highlight-var-chart';
      }
      return 'var-chart';
    },
    getVariableAbbreviation(v) {
      var abbrev = this.useLongScaleNames ? v.label : v.abbreviation;
      if (v.parentLabel && v.parentLabel != abbrev) {
        return `${v.parentLabel} - ${abbrev}`;
      }
      return abbrev;
    },
    getVariableLabel(v) {
      var lbl = this.useLongScaleNames ? v.description : v.label;
      if (v.parentLabel && v.parentLabel != lbl) {
        return `${v.parentLabel} - ${lbl}`;
      }
      return lbl;
    },
    getChartTitle(v, which) {
      let which_final = null;
      if (which == 'abbreviation') {
        which_final = this.useLongScaleNames ? 'label' : 'abbreviation';
      } else {
        which_final = this.useLongScaleNames ? 'description' : 'label';
      }
      var title = v[which_final];

      let chartVars = [...this.chartVariables];
      if (chartVars.length > 1) {
        chartVars.sort().forEach(cv => {
          let m = cv[which].match(/^((\S+)_(org)?)(\d+)$/i);
          if (m) {
            if (m[4] == 1) {
              title = m[1];
              if (title.endsWith('_')) {
                title = title.substring(0, title.length - 1);
              }
            }
          }
        });
      }

      if (v.type == 'observation' && v.is_longitudinal) {
        var fv = null;
        var lv = null;
        var p_name = null;
        let parentID = 'parentID' in v ? v.parentID : v.parent_id;

        this.collection.observation_variables_list.forEach(ov => {
          if (ov.ontology.id == parentID) {
            if (ov['first_visit_event']) {
              fv = ov['first_visit_event'];
              lv = ov['last_visit_event'];
              p_name = ov['ontology'][which_final];
            } else {
              fv = ov['first_visit_num'];
              lv = ov['last_visit_num'];
              p_name = ov['ontology'][which_final];
            }
          }
        });
        if (v.label == 'First Visit') {
          title = p_name + ' - ' + (which == 'label' ? 'Visit=' : '') + fv;
        } else if (this.variable.label == 'Last Visit') {
          title = p_name + ' - ' + (which == 'label' ? 'Visit=' : '') + lv;
        } else {
          var label = v.label;
          if (label == 'Rate of Change' && which == 'abbreviation') {
            label = 'Rate of Change';
          }
          if (p_name != null) {
            title = p_name + ' - ' + label + ': ' + fv + '-' + lv;
          }
        }
      }
      return title;
    },
    getVisitLabel(isFirst) {
      var which = isFirst ? 'first' : 'last';
      var result = '?';
      this.collection.observation_variables_list.forEach(ov => {
        if (ov.ontology.id == this.variable.id) {
          if (ov[which + '_visit_event']) {
            result = String(ov[which + '_visit_event']);
          } else {
            result = String(ov[which + '_visit_num']);
          }
        }
      });
      return result;
    },
    getFirstVisitLabel() {
      return this.getVisitLabel(true);
    },
    getLastVisitLabel() {
      return this.getVisitLabel(false);
    },
    getSelectedMeasuresKey(v) {
      var key = '';
      if (!('selected_measures' in v)) {
        return 'all';
      }

      ['firstVisit', 'lastVisit', 'change', 'roc'].forEach(m => {
        if (m in v['selected_measures'] && v['selected_measures'][m]) {
          key += '1';
        } else {
          key += '0';
        }
      });
      return key;
    },
    // returns all variables in the group for grouped vars
    getChartVariables(v) {
      let v_list = [];
      const sv2g = this.subjectVariableGroups['var2group'];
      const ov2g = this.observationVariableGroups['var2group'];

      if (v.label in sv2g) {
        sv2g[v.label]['vars'].forEach(v => {
          v_list.push(v.var);
        });
      } else if (v.label in ov2g) {
        ov2g[v.label]['vars'].forEach(v => {
          v_list.push(v.var);
        });
      } else {
        v_list.push(v);
      }
      return v_list;
    },
  },
};
</script>

<style lang="scss" scoped></style>
